/**
 * Message contract between the custom service worker (`src/sw.ts`) and the app window
 * (`NetworkStatusProvider`). The SW owns the durable Background-Sync queue in IndexedDB; these
 * messages let the SW broadcast queue lifecycle so the UI can mirror what is queued / posting /
 * posted / failed without persisting (and drifting from) the durable queue.
 */

export const SW_MESSAGE_TYPE = {
	QUEUE_REPLAY_COMPLETE: "QUEUE_REPLAY_COMPLETE",
	QUEUE_REPLAY_ENQUEUED: "QUEUE_REPLAY_ENQUEUED",
	QUEUE_REPLAY_FAILED: "QUEUE_REPLAY_FAILED",
	QUEUE_REPLAY_STARTED: "QUEUE_REPLAY_STARTED",
	QUEUE_REPLAY_STATE_SNAPSHOT: "QUEUE_REPLAY_STATE_SNAPSHOT",
	QUEUE_REPLAY_SUCCESS: "QUEUE_REPLAY_SUCCESS",
	REQUEST_QUEUE_STATE: "REQUEST_QUEUE_STATE",
	SKIP_WAITING: "SKIP_WAITING"
} as const;

export type SwMessageType = (typeof SW_MESSAGE_TYPE)[keyof typeof SW_MESSAGE_TYPE];

/** Minimal queue-entry descriptor surfaced to the UI for the history/retry view. */
export interface QueueEntryDescriptor {
	clientId: string;
	mutationName: string;
	/** See `MutationMetadata.missionEntryId` (helpers/sw/requestHandling.ts). */
	missionEntryId?: string;
}

export interface QueueReplayStartedMessage {
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED;
}

export interface QueueReplayCompleteMessage {
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE;
}

export interface QueueReplayEnqueuedMessage {
	clientId: string;
	mutationName: string;
	missionEntryId?: string;
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_ENQUEUED;
}

export interface QueueReplaySuccessMessage {
	clientId: string;
	mutationName: string;
	missionEntryId?: string;
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS;
}

export interface QueueReplayFailedMessage {
	clientId: string;
	mutationName: string;
	missionEntryId?: string;
	reason: string;
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED;
}

export interface QueueReplayStateSnapshotMessage {
	entries: QueueEntryDescriptor[];
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT;
}

export type SwOutboundMessage =
	| QueueReplayCompleteMessage
	| QueueReplayEnqueuedMessage
	| QueueReplayFailedMessage
	| QueueReplayStartedMessage
	| QueueReplayStateSnapshotMessage
	| QueueReplaySuccessMessage;

const isSwOutboundMessage = (data: unknown): data is SwOutboundMessage => {
	if (!data || typeof data !== "object") return false;
	const type = (data as { type?: unknown }).type;
	if (
		type !== SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED &&
		type !== SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE &&
		type !== SW_MESSAGE_TYPE.QUEUE_REPLAY_ENQUEUED &&
		type !== SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS &&
		type !== SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED &&
		type !== SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT
	) {
		return false;
	}
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED) return true;
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE) return true;
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT) {
		return Array.isArray((data as { entries?: unknown }).entries);
	}
	const { clientId, mutationName } = data as { clientId?: unknown; mutationName?: unknown };
	if (typeof clientId !== "string" || typeof mutationName !== "string") return false;
	const missionEntryId = (data as { missionEntryId?: unknown }).missionEntryId;
	if (missionEntryId !== undefined && typeof missionEntryId !== "string") return false;
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED) {
		return typeof (data as { reason?: unknown }).reason === "string";
	}
	return true;
};

export default isSwOutboundMessage;
